"use client"
import { useState } from "react"
import Script from "next/script"
import { zeTrack } from "@/components/Analytics"

const TURNSTILE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

interface ViewingFormProps {
  listingId: string
  listingTitle: string
}

const SLOTS = ["8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"]

export default function ViewingForm({ listingId, listingTitle }: ViewingFormProps) {
  const [form, setForm] = useState({ name: "", phone: "", requestedDate: "", requestedSlot: "", isVideo: false, message: "" })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState("")

  const minDate = new Date()
  minDate.setDate(minDate.getDate() + 1)
  const minDateStr = minDate.toISOString().split("T")[0]

  async function handleSubmit() {
    if (!form.name || !form.phone) { setError("Please enter your name and phone number"); return }
    if (!form.requestedDate) { setError("Please select a viewing date"); return }
    if (!form.requestedSlot) { setError("Please select a time slot"); return }
    setSubmitting(true)
    setError("")
    try {
      let phone = form.phone.replace(/\s/g, "")
      if (phone.startsWith("0")) phone = "+263" + phone.slice(1)
      else if (!phone.startsWith("+")) phone = "+263" + phone
      // Auth rides on the session cookie (middleware injects x-user-id from the JWT);
      // anonymous requests must pass the Turnstile challenge below.
      const turnstileToken = (window as any).turnstile?.getResponse() ?? ""
      const res = await fetch("/api/viewings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ listingId, name: form.name, phone, requestedDate: form.requestedDate, requestedSlot: form.requestedSlot, isVideo: form.isVideo, message: form.message, "cf-turnstile-response": turnstileToken }),
      })
      const data = await res.json()
      if (data.success) {
        setSubmitted(true)
        zeTrack("viewing_requested", { listing_id: listingId, is_video: form.isVideo })
      } else {
        setError(data.error ?? "Something went wrong")
        ;(window as any).turnstile?.reset()
      }
    } catch {
      setError("Something went wrong. Try WhatsApp instead.")
    }
    setSubmitting(false)
  }

  if (submitted) return (
    <div style={{ background: "rgba(61,139,95,0.06)", border: "1px solid rgba(61,139,95,0.2)", borderRadius: 14, padding: 24, textAlign: "center" }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
      <div style={{ fontWeight: 700, color: "var(--green-text)", marginBottom: 6, fontSize: 16 }}>Viewing requested</div>
      <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.5 }}>The agent will confirm your viewing via WhatsApp or call within 2 hours.</div>
    </div>
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
        <button onClick={() => setForm(f => ({ ...f, isVideo: false }))} style={{ flex: 1, padding: "8px", borderRadius: 8, border: "1px solid " + (!form.isVideo ? "var(--navy)" : "var(--line)"), background: !form.isVideo ? "var(--navy)" : "var(--cream)", color: !form.isVideo ? "var(--cream)" : "var(--navy)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>In-person</button>
        <button onClick={() => setForm(f => ({ ...f, isVideo: true }))} style={{ flex: 1, padding: "8px", borderRadius: 8, border: "1px solid " + (form.isVideo ? "var(--green)" : "var(--line)"), background: form.isVideo ? "rgba(61,139,95,0.08)" : "var(--cream)", color: form.isVideo ? "var(--green)" : "var(--navy)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Video tour</button>
      </div>
      <div>
        <label htmlFor="viewing-name" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>Full name</label>
        <input id="viewing-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your full name" autoComplete="name" style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
      </div>
      <div>
        <label htmlFor="viewing-phone" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>Phone number</label>
        <div style={{ display: "flex", gap: 0 }}>
          <div style={{ padding: "12px 12px", border: "1px solid var(--line)", borderRight: "none", borderRadius: "8px 0 0 8px", fontSize: 13, color: "var(--navy)", fontWeight: 600, background: "var(--cream-warm)", whiteSpace: "nowrap", display: "flex", alignItems: "center" }}>+263</div>
          <input id="viewing-phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="77 234 5678" type="tel" autoComplete="tel" style={{ flex: 1, padding: "12px 14px", border: "1px solid var(--line)", borderRadius: "0 8px 8px 0", fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
        </div>
      </div>
      <div>
        <label htmlFor="viewing-date" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>Preferred date</label>
        <input id="viewing-date" type="date" value={form.requestedDate} min={minDateStr} onChange={e => setForm(f => ({ ...f, requestedDate: e.target.value }))} style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
      </div>
      <div>
        <p style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)", marginBottom: 6 }}>Preferred time</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {SLOTS.map(slot => (
            <button key={slot} type="button" aria-pressed={form.requestedSlot === slot} onClick={() => setForm(f => ({ ...f, requestedSlot: slot }))} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid " + (form.requestedSlot === slot ? "var(--navy)" : "var(--line)"), background: form.requestedSlot === slot ? "var(--navy)" : "var(--cream)", color: form.requestedSlot === slot ? "var(--cream)" : "var(--navy)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{slot}</button>
          ))}
        </div>
      </div>
      <div>
        <label htmlFor="viewing-message" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>Message <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span></label>
        <textarea id="viewing-message" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Anything you would like the agent to know..." rows={2} style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "var(--cream)", outline: "none", resize: "vertical" }} />
      </div>
      {error && <div role="alert" style={{ fontSize: 13, color: "var(--red-text)", padding: "8px 12px", background: "rgba(196,68,68,0.06)", borderRadius: 8 }}>{error}</div>}
      {TURNSTILE_KEY && (
        <>
          <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" strategy="lazyOnload" />
          <div className="cf-turnstile" data-sitekey={TURNSTILE_KEY} data-error-callback="zeTurnstileError" data-retry="auto" data-theme="light" />
        </>
      )}
      <button onClick={handleSubmit} disabled={submitting} style={{ width: "100%", padding: "13px", background: submitting ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
        {submitting ? "Sending..." : form.isVideo ? "Request video tour" : "Request viewing"}
      </button>
    </div>
  )
}
